import * as React from 'react'
import { customerMarketShopfrontApi, Site } from '@envato/foundation-types'
import Button, { ButtonAppearance, ButtonSize } from '@envato/foundation-button'
import { get } from 'lodash-es'
import { IItem } from '../../../../typings/searchResults'
import { ISrcSetImages } from '../../../../common/utils/imageSrcSetHelpers'
import { getConfigBySite } from '../../../../config/sites'
import { reportCategoryPageClick } from '../../../../common/analytics/categoryPageAnalytics'
import { siteAbbreviations } from '../../../../common/constants/siteAbbreviations'
import { IBlockMatchedComponent } from '../../../pages/category/category'
import FeatureContainer from '../../../containers/feature'
import OriginalElementsPromoItemsBlock from './originalElementsPromoItemsBlock'
import Card from './card/card'
import AudioCard from './audioCard/audioCard'

import * as styles from './elementsPromoItemsBlock.module.css'

export interface IPromoData {
  marketID: number
  previews: IItem['previews']
  title: string
  author: string
  itemURL: string
  categoryName?: string
  type?: string
  image_urls?: ISrcSetImages[]
}

export interface IImageRatio {
  previewRatio: number
  imageRatio?: number
}

export interface ICard extends IPromoData {
  width: number
  imageRatios: IImageRatio
  imageURL?: string
}

interface IProps extends IBlockMatchedComponent {
  data: IPromoData[]
  title: string
  cta: customerMarketShopfrontApi.categoryPage.ICta
}

const ratioByType: { [type: string]: number } = {
  graphic: 1,
  video: 9 / 16,
  code: 300 / 590,
  theme: 300 / 590,
  audio: 2 / 3
}

const getRatios = (item: IPromoData, currentSite: Site) => {
  const defaultRatio = getConfigBySite(currentSite, 'ui.image_preview_ratio')
  let previewRatio
  let imageRatio

  if (item.type === 'photo') {
    const height = get(item, 'image_urls[0].height', defaultRatio)
    const width = get(item, 'image_urls[0].width', defaultRatio)
    previewRatio = height / width
    imageRatio = width / height

    if (previewRatio > 1) {
      previewRatio = 1
    } else if (previewRatio < 0.5625) {
      previewRatio = 0.5625
    }
  } else {
    previewRatio = (item.type && ratioByType[item.type]) || defaultRatio
  }

  return {
    previewRatio,
    imageRatio
  }
}

export const addUTMParamsToURL = (url: string, currentSite: Site) => {
  const siteAbbreviation = siteAbbreviations[currentSite]
  return `${url}?utm_campaign=elements_mkt-search_categorygenericbanner&utm_content=${siteAbbreviation}_promo-block&utm_medium=promos&utm_source=${currentSite}.net`
}

export const ElementsPromoItemsBlock = ({
  data,
  title,
  subtitle,
  cta,
  currentSite
}: IProps) => {
  const itemsWithRatios = data.map(item => {
    const imageRatios = getRatios(item, currentSite)
    return {
      ...item,
      itemURL: addUTMParamsToURL(item.itemURL, currentSite),
      widthRatio: 1 / imageRatios.previewRatio,
      imageRatios
    }
  })

  const sumOfRatios: number = itemsWithRatios.reduce(
    (acc, item) => acc + item.widthRatio,
    0
  )

  return (
    <section className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.content}>
          <header>
            <h2 className={styles.title}>{title}</h2>
            {subtitle && <h3 className={styles.subtitle}>{subtitle}</h3>}
          </header>
          <Button
            className={styles.button}
            appearance={ButtonAppearance.Primary}
            size={ButtonSize.Small}
            href={addUTMParamsToURL(cta.url, currentSite)}
            onClick={reportCategoryPageClick('traffic to elements')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {cta.title}
          </Button>
        </div>
        <div className={styles.items}>
          {itemsWithRatios.map((item, index) => {
            const width = (item.widthRatio / sumOfRatios) * 100
            return (
              <div
                className={styles.item}
                key={index}
                style={{ flexBasis: `${width}%` }}
              >
                {item.type === 'audio' ? (
                  <AudioCard {...item} width={width} />
                ) : (
                  <Card {...item} width={width} />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default ({ ...props }: IProps) => (
  <>
    <FeatureContainer name="showOriginalBlock">
      <OriginalElementsPromoItemsBlock {...props} />
    </FeatureContainer>
    <FeatureContainer name="!showOriginalBlock">
      <ElementsPromoItemsBlock {...props} />
    </FeatureContainer>
  </>
)
